import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Plug, Wrench } from 'lucide-react';
import { fetchFacilitiesSites, getDisplayErrorMessage } from '@/lib/api';
import {
  OFFLINE_STEPS, REFUSED_START_STEPS, SAFETY_NOTE, alertLine, alertTitle, chargerState, formatWhen, orderAlerts,
} from '@/lib/facilitiesView';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import siteStyles from '../site/SitePages.module.css';
import styles from './FacilitiesPage.module.css';

/**
 * The building staff page (Warren, 28 Oct 2026, item 5): their sites'
 * chargers, live status and alerts, and the steps for a breaker flip or a
 * restart. Nothing else: no driver, no price, no other location (the Api's
 * GET /facilities/sites carries none, and lib/facilitiesOnlyGate.ts holds a
 * building staff login to it).
 *
 * Refreshes every 30 seconds. An alert link (/alerts/:id, the bell's) opens
 * here with that alert highlighted.
 */
export function FacilitiesPage({ focusAlertId }: { focusAlertId?: string }) {
  useRecentRoute('My chargers');
  const query = useQuery({
    queryKey: ['facilities-sites'],
    queryFn: fetchFacilitiesSites,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const focusRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (focusAlertId && query.data && focusRef.current) focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusAlertId, query.data]);

  if (query.isPending) return <div className={siteStyles.page}><p className={siteStyles.muted}>Loading your chargers...</p></div>;
  if (query.isError) {
    return (
      <div className={siteStyles.page} role="alert">
        <ErrorLine error={query.error} />
        <button type="button" onClick={() => void query.refetch()}>Retry</button>
      </div>
    );
  }
  const { sites, checked_at: checkedAt } = query.data;
  const now = new Date(checkedAt);

  return (
    <div className={siteStyles.page}>
      <header className={siteStyles.header}>
        <h1 className={siteStyles.title}>My chargers</h1>
        <span className={siteStyles.muted}>Updated {formatWhen(checkedAt, null, now)}. Refreshes every 30 seconds.</span>
      </header>

      {sites.length === 0 && <p className={siteStyles.muted}>No location is shared with you yet.</p>}

      {sites.map((site) => {
        const alerts = orderAlerts(site.alerts);
        const chargerName = new Map(site.chargers.map((c) => [c.id, c.name]));
        const offlineCount = site.chargers.filter((c) => !c.online).length;
        return (
          <section key={site.id} className={siteStyles.card} aria-labelledby={`site-${site.id}`}>
            <div className={siteStyles.cardHead}>
              <Plug size={18} aria-hidden />
              <h2 id={`site-${site.id}`} className={siteStyles.cardTitle}>{site.name}</h2>
            </div>
            {site.address && <p className={siteStyles.muted}>{site.address}</p>}
            <p className={offlineCount ? styles.summaryBad : styles.summaryOk}>
              {site.chargers.length === 0
                ? 'No active chargers here.'
                : offlineCount === 0
                  ? `All ${site.chargers.length} charger${site.chargers.length === 1 ? ' is' : 's are'} online.`
                  : `${offlineCount} of ${site.chargers.length} charger${site.chargers.length === 1 ? ' is' : 's are'} offline.`}
            </p>

            <ul className={siteStyles.list}>
              {site.chargers.map((c) => {
                const state = chargerState(c);
                return (
                  <li key={c.id} className={siteStyles.listRow}>
                    <span className={styles.who}>
                      <span className={siteStyles.rowName}>{c.name}</span>
                      <span className={styles.sub}>
                        {c.name !== c.identity ? `${c.identity} · ` : ''}last heard {formatWhen(c.last_heard_at, site.timezone, now)}
                      </span>
                    </span>
                    <span className={state.ok ? siteStyles.badgeOk : siteStyles.badgeOff}>{state.label}</span>
                  </li>
                );
              })}
            </ul>

            <h3 className={styles.subTitle}><AlertTriangle size={15} aria-hidden /> Alerts</h3>
            {alerts.length === 0
              ? <p className={siteStyles.muted}>No alerts in the last 7 days.</p>
              : (
                <ul className={styles.alerts}>
                  {alerts.map((a) => (
                    <li
                      key={a.id}
                      ref={a.id === focusAlertId ? focusRef : undefined}
                      className={`${styles.alert} ${a.open ? styles.alertOpen : ''} ${a.id === focusAlertId ? styles.alertFocus : ''}`}
                    >
                      <span className={styles.alertTitle}>{alertTitle(a)}: {chargerName.get(a.charger_id) ?? 'a charger'}</span>
                      <span className={styles.sub}>{alertLine(a, site.timezone, now)}</span>
                    </li>
                  ))}
                </ul>
              )}
          </section>
        );
      })}

      <section className={siteStyles.card} aria-labelledby="facilities-steps">
        <div className={siteStyles.cardHead}>
          <Wrench size={18} aria-hidden />
          <h2 id="facilities-steps" className={siteStyles.cardTitle}>If a charger is offline</h2>
        </div>
        <ol className={styles.steps}>{OFFLINE_STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
        <h3 className={styles.subTitle}>If a charger keeps refusing to start</h3>
        <ol className={styles.steps}>{REFUSED_START_STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
        <p className={siteStyles.muted}>{SAFETY_NOTE}</p>
      </section>
    </div>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  const [message, setMessage] = useState('Could not load your chargers.');
  useEffect(() => {
    let live = true;
    void getDisplayErrorMessage(error, 'Could not load your chargers.').then((m) => { if (live) setMessage(m); });
    return () => { live = false; };
  }, [error]);
  return <p>{message}</p>;
}

export default FacilitiesPage;
